import React, { createContext, useContext, useState } from 'react';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const DEFAULT_COLLAPSED_WIDTH = 76;

interface SidebarContextValue {
  collapsed: boolean;
  surface: 'mobile' | 'desktop';
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  surface: 'desktop',
});

const useSidebarContext = (): SidebarContextValue => useContext(SidebarContext);

/** Which drawer surface is rendering this subtree. */
export const useSidebarSurface = (): 'mobile' | 'desktop' =>
  useSidebarContext().surface;

export interface SidebarProps {
  width: number;
  collapsedWidth?: number;
  collapsed?: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  children: React.ReactNode;
}

const SidebarRoot: React.FC<SidebarProps> = ({
  width,
  collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
  collapsed = false,
  mobileOpen,
  onMobileClose,
  children,
}) => {
  const theme = useTheme();
  const desktopWidth = collapsed ? collapsedWidth : width;

  const widthTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const paperBaseSx = {
    boxSizing: 'border-box' as const,
    bgcolor: theme.palette.background.paper,
    borderRight: 'none',
    overflowX: 'hidden' as const,
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: desktopWidth },
        flexShrink: { sm: 0 },
        transition: { sm: widthTransition },
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { ...paperBaseSx, width },
        }}
      >
        <SidebarContext.Provider value={{ collapsed: false, surface: 'mobile' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            ...paperBaseSx,
            width: desktopWidth,
            transition: widthTransition,
          },
        }}
      >
        <SidebarContext.Provider value={{ collapsed, surface: 'desktop' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>
    </Box>
  );
};

export interface SidebarHeaderProps {
  children: React.ReactNode;
  collapsedChildren?: React.ReactNode;
}

const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  children,
  collapsedChildren,
}) => {
  const { collapsed } = useSidebarContext();

  return (
    <Toolbar
      sx={{
        px: 2,
        py: 3,
        overflow: 'hidden',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {collapsed ? collapsedChildren ?? children : children}
    </Toolbar>
  );
};

const SidebarExpandedOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { collapsed } = useSidebarContext();
  return collapsed ? null : <>{children}</>;
};

const SidebarNav: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <List component="nav" disablePadding sx={{ py: 1.25 }}>
    {children}
  </List>
);

export interface SidebarNavGroupProps {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  label,
  defaultOpen = true,
  children,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) {
    return <Box sx={{ '& + &': { mt: 1 } }}>{children}</Box>;
  }

  return (
    <Box sx={{ mb: 1, '&:first-of-type': { mt: 0 }, mt: 1.5 }}>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        disableRipple
        sx={{
          mx: 1.5,
          px: 1,
          py: 0.5,
          mb: 0.5,
          borderRadius: '10px',
          '&:hover': { background: 'transparent' },
        }}
      >
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            variant: 'overline',
            sx: {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              color: theme.palette.text.secondary,
            },
          }}
        />
        {open ? (
          <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </Box>
  );
};

export interface SidebarNavItemProps {
  id?: string;
  icon: React.ReactNode;
  label: string;
  selected?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Extra attributes on the clickable button (e.g. data-* for menu anchors). */
  buttonProps?: React.ButtonHTMLAttributes<HTMLDivElement> & {
    'data-account-menu-trigger'?: string;
  };
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  id,
  icon,
  label,
  selected = false,
  onClick,
  buttonProps,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const tooltipPlacement = theme.direction === 'rtl' ? 'left' : 'right';

  const button = (
    <ListItemButton
      id={id}
      selected={selected}
      onClick={onClick}
      {...buttonProps}
      sx={{
        minHeight: 46,
        borderRadius: '12px',
        color: theme.palette.text.secondary,
        transition: theme.transitions.create(
          ['background-color', 'color', 'box-shadow'],
          { duration: theme.transitions.duration.shorter },
        ),
        ...(collapsed
          ? {
              width: 46,
              height: 46,
              p: 0,
              mx: 'auto',
              justifyContent: 'center',
            }
          : { px: 1.75, py: 1, justifyContent: 'flex-start' }),
        '& .MuiListItemIcon-root': {
          minWidth: collapsed ? 0 : 38,
          color: 'inherit',
          justifyContent: 'center',
          transition: 'inherit',
          '& .MuiSvgIcon-root': { fontSize: 22 },
          '& .MuiBadge-root': {
            display: 'inline-flex',
          },
          '& .MuiBadge-badge': {
            fontSize: '0.65rem',
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            fontWeight: 700,
          },
        },
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}14`,
          color: theme.palette.primary.main,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: `0 6px 16px ${theme.palette.primary.main}3D`,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
          },
          '& .MuiListItemIcon-root': {
            color: theme.palette.primary.contrastText,
          },
          '& .MuiListItemIcon-root .MuiBadge-badge': {
            border: `2px solid ${theme.palette.primary.main}`,
          },
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: '0.9rem', fontWeight: selected ? 700 : 500 },
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem
      disablePadding
      sx={{ display: 'block', px: collapsed ? 0 : 1.5, mb: 0.5 }}
    >
      {collapsed ? (
        <Tooltip title={label} placement={tooltipPlacement}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

export interface SidebarCollapseToggleProps {
  onClick: () => void;
  expandLabel?: string;
  collapseLabel?: string;
}

const SidebarCollapseToggle: React.FC<SidebarCollapseToggleProps> = ({
  onClick,
  expandLabel = 'Expand menu',
  collapseLabel = 'Collapse menu',
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const label = collapsed ? expandLabel : collapseLabel;
  const tooltipPlacement = theme.direction === 'rtl' ? 'left' : 'right';
  const isRtl = theme.direction === 'rtl';

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        justifyContent: collapsed ? 'center' : 'flex-end',
        px: 1.5,
        py: 1,
      }}
    >
      <Tooltip title={label} placement={tooltipPlacement}>
        <IconButton
          onClick={onClick}
          size="small"
          aria-label={label}
          sx={{
            color: theme.palette.text.secondary,
            borderRadius: '10px',
            transition: theme.transitions.create(['background-color', 'color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}14`,
              color: theme.palette.primary.main,
            },
          }}
        >
          {collapsed ? (isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />) : isRtl ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const Sidebar = Object.assign(SidebarRoot, {
  Header: SidebarHeader,
  ExpandedOnly: SidebarExpandedOnly,
  Nav: SidebarNav,
  NavGroup: SidebarNavGroup,
  NavItem: SidebarNavItem,
  CollapseToggle: SidebarCollapseToggle,
});

export default Sidebar;
